import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as api from '../services/api';
import { StatusBadge } from '../components/StatusBadge';
import { Modal } from '../components/Modal';
import { ConfirmNoteModal } from '../components/ConfirmNoteModal';
import { FilterBar } from '../components/FilterBar';
import { useAuth } from '../context/AuthContext';
import type { LifecycleAction, ManagedUser, UserRole, UserStatus } from '../types/auth';

type UserTab = 'teachers' | 'students' | 'school_admins' | 'all';

const STATUS_OPTIONS: Array<UserStatus | ''> = ['', 'pending', 'active', 'suspended', 'rejected'];

const PAGE_SIZE = 25;

function availableLifecycleActions(status: UserStatus): LifecycleAction[] {
  switch (status) {
    case 'pending':
      return ['activate', 'reject'];
    case 'active':
      return ['deactivate', 'suspend', 'reject'];
    case 'suspended':
      return ['activate', 'deactivate', 'reject'];
    case 'rejected':
      return ['activate'];
    default:
      return [];
  }
}

const emptyRegisterForm = {
  firstName: '',
  middleName: '',
  lastName: '',
  username: '',
  email: '',
  password: '',
  phone: '',
  age: '',
  gender: 'male' as 'male' | 'female',
  cnic: ''
};

function tabToRole(tab: UserTab): UserRole | '' {
  if (tab === 'teachers') return 'TEACHER';
  if (tab === 'students') return 'STUDENT';
  if (tab === 'school_admins') return 'SCHOOL_ADMIN';
  return '';
}

function parseTab(value: string | null, isSuperAdmin: boolean): UserTab {
  if (value === 'teachers' || value === 'students' || value === 'school_admins') return value;
  if (value === 'all' && isSuperAdmin) return 'all';
  return isSuperAdmin ? 'all' : 'teachers';
}

export function UsersPage() {
  const { user, isSuperAdmin, isSchoolAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const schoolId = user?.schoolId;

  const [tab, setTab] = useState<UserTab>(() =>
    parseTab(searchParams.get('tab'), isSuperAdmin)
  );
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [registering, setRegistering] = useState(false);

  const [noteTarget, setNoteTarget] = useState<{
    user: ManagedUser;
    action: LifecycleAction;
  } | null>(null);

  const role = tabToRole(tab);
  const canRegister =
    Boolean(schoolId) && (tab === 'teachers' || tab === 'students') && (isSchoolAdmin || isSuperAdmin);

  const tabs = useMemo(
    () =>
      isSuperAdmin
        ? ([
            { id: 'all' as const, label: 'All' },
            { id: 'school_admins' as const, label: 'School admins' },
            { id: 'teachers' as const, label: 'Teachers' },
            { id: 'students' as const, label: 'Students' }
          ] as const)
        : ([
            { id: 'teachers' as const, label: 'Teachers' },
            { id: 'students' as const, label: 'Students' },
            { id: 'school_admins' as const, label: 'School admins' }
          ] as const),
    [isSuperAdmin]
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'all' || (tab === 'teachers' && !isSuperAdmin)) next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true }
    );
  }, [tab, isSuperAdmin, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.listUsers({
          role: role || undefined,
          status,
          search,
          page,
          limit: PAGE_SIZE,
          schoolId: isSchoolAdmin && schoolId ? schoolId : schoolFilter.trim() || undefined
        });
        if (!cancelled) {
          setUsers(result.items);
          setTotal(result.total);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load users');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [role, status, search, page, isSchoolAdmin, schoolId, schoolFilter]);

  function handleSearch(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  async function confirmLifecycle(note: string) {
    if (!noteTarget) return;
    const { user: target, action } = noteTarget;
    setBusyUserId(target.id);
    setError(null);
    setSuccess(null);
    try {
      const updated = await api.applyLifecycleAction(target.id, action, note || undefined);
      setUsers((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setSuccess(`${action} applied to ${updated.fullName}.`);
      setNoteTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} user`);
    } finally {
      setBusyUserId(null);
    }
  }

  async function handleRegister(event: FormEvent) {
    event.preventDefault();
    if (!schoolId || (tab !== 'teachers' && tab !== 'students')) return;

    const age = Number(registerForm.age);
    if (!Number.isFinite(age)) {
      setError('Age is required');
      return;
    }

    setRegistering(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        firstName: registerForm.firstName.trim(),
        middleName: registerForm.middleName.trim() || undefined,
        lastName: registerForm.lastName.trim(),
        username: registerForm.username.trim(),
        email: registerForm.email.trim(),
        password: registerForm.password,
        phone: registerForm.phone.trim() || undefined,
        age,
        gender: registerForm.gender,
        cnic: registerForm.cnic.trim(),
        schoolId
      };

      const result =
        tab === 'teachers'
          ? await api.registerTeacher(payload)
          : await api.registerStudent(payload);

      setRegisterForm(emptyRegisterForm);
      setShowRegister(false);
      setSuccess(
        tab === 'teachers'
          ? `Registered teacher ${result.user.fullName} (pending until activated).`
          : `Registered student ${result.user.fullName}.`
      );
      setPage(1);
      const refreshed = await api.listUsers({
        role: role || undefined,
        status,
        search,
        page: 1,
        limit: PAGE_SIZE,
        schoolId
      });
      setUsers(refreshed.items);
      setTotal(refreshed.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setRegistering(false);
    }
  }

  function updateRegisterField<K extends keyof typeof emptyRegisterForm>(
    key: K,
    value: (typeof emptyRegisterForm)[K]
  ) {
    setRegisterForm((prev) => ({ ...prev, [key]: value }));
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <section className="admin-panel schools-page">
      <div className="panel-heading">
        <div>
          <h2>{isSuperAdmin ? 'Users' : 'School users'}</h2>
          <p>
            {isSuperAdmin
              ? 'Manage platform identities by role. Lifecycle actions follow user-service RBAC.'
              : 'Manage teachers, students, and other school admins in your school.'}
          </p>
        </div>
        <span className="panel-meta">{total} total</span>
      </div>

      <div className="tab-bar" role="tablist" aria-label="User roles">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={tab === item.id ? 'tab-btn active' : 'tab-btn'}
            onClick={() => {
              setTab(item.id);
              setPage(1);
              setShowRegister(false);
              setError(null);
              setSuccess(null);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <FilterBar onSubmit={handleSearch}>
        <label>
          Search
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name, email, username"
          />
        </label>
        <label>
          Status
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value as UserStatus | '');
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option || 'all'} value={option}>
                {option || 'All statuses'}
              </option>
            ))}
          </select>
        </label>
        {isSuperAdmin ? (
          <label>
            School ID
            <input
              type="search"
              value={schoolFilter}
              onChange={(e) => {
                setPage(1);
                setSchoolFilter(e.target.value.trim());
              }}
              placeholder="Optional school id"
            />
          </label>
        ) : null}
        <button type="submit" className="primary-btn">
          Apply
        </button>
        {canRegister ? (
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              setShowRegister(true);
              setError(null);
              setSuccess(null);
            }}
          >
            {tab === 'teachers' ? 'Register teacher' : 'Register student'}
          </button>
        ) : null}
      </FilterBar>

      {tab === 'school_admins' && isSchoolAdmin ? (
        <p className="muted">
          You can review and apply lifecycle actions to other school admins in your school. Creating
          additional school-admin accounts for an existing school is not available in the API yet.
        </p>
      ) : null}

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status">
          {success}
        </p>
      ) : null}

      {loading ? <p className="muted">Loading users…</p> : null}
      {!loading && users.length === 0 ? <p className="muted">No users match these filters.</p> : null}

      {users.length > 0 ? (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.fullName}</strong>
                      <div className="cell-sub">{item.username}</div>
                    </td>
                    <td>{item.email}</td>
                    <td>{item.role}</td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td>{item.isActive ? 'Yes' : 'No'}</td>
                    <td>
                      <div className="action-row">
                        {availableLifecycleActions(item.status).map((action) => (
                          <button
                            key={action}
                            type="button"
                            className="ghost-btn compact"
                            disabled={
                              busyUserId === item.id ||
                              item.role === 'SUPER_ADMIN' ||
                              item.id === user?.id
                            }
                            onClick={() => setNoteTarget({ user: item, action })}
                          >
                            {action}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="school-list-pager" role="navigation" aria-label="Users pagination">
            <span className="school-list-pager-range">
              {rangeStart}-{rangeEnd} of {total}
            </span>
            <button
              type="button"
              className="pager-chevron"
              aria-label="Previous page"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ‹
            </button>
            <button
              type="button"
              className="pager-chevron"
              aria-label="Next page"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              ›
            </button>
          </div>
        </>
      ) : null}

      <Modal
        open={showRegister && canRegister}
        title={tab === 'teachers' ? 'Register teacher' : 'Register student'}
        subtitle={
          tab === 'teachers'
            ? 'Creates a TEACHER account for your school (pending until activated).'
            : 'Creates a STUDENT account for your school.'
        }
        size="lg"
        onClose={() => {
          if (!registering) setShowRegister(false);
        }}
        closeDisabled={registering}
        asForm
        onSubmit={(event) => void handleRegister(event)}
        actions={
          <>
            <button
              type="button"
              className="ghost-btn"
              disabled={registering}
              onClick={() => setShowRegister(false)}
            >
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={registering}>
              {registering
                ? 'Registering…'
                : tab === 'teachers'
                  ? 'Register teacher'
                  : 'Register student'}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <label>
            First name
            <input
              required
              value={registerForm.firstName}
              onChange={(e) => updateRegisterField('firstName', e.target.value)}
            />
          </label>
          <label>
            Middle name
            <input
              value={registerForm.middleName}
              onChange={(e) => updateRegisterField('middleName', e.target.value)}
            />
          </label>
          <label>
            Last name
            <input
              required
              value={registerForm.lastName}
              onChange={(e) => updateRegisterField('lastName', e.target.value)}
            />
          </label>
          <label>
            Username
            <input
              required
              autoComplete="off"
              value={registerForm.username}
              onChange={(e) => updateRegisterField('username', e.target.value)}
            />
          </label>
          <label>
            Email
            <input
              required
              type="email"
              autoComplete="off"
              value={registerForm.email}
              onChange={(e) => updateRegisterField('email', e.target.value)}
            />
          </label>
          <label>
            Password
            <input
              required
              type="password"
              autoComplete="new-password"
              minLength={6}
              value={registerForm.password}
              onChange={(e) => updateRegisterField('password', e.target.value)}
            />
          </label>
          <label>
            Phone
            <input
              value={registerForm.phone}
              onChange={(e) => updateRegisterField('phone', e.target.value)}
            />
          </label>
          <label>
            Age
            <input
              required
              type="number"
              min={tab === 'students' ? 3 : 18}
              max={tab === 'students' ? 25 : 120}
              value={registerForm.age}
              onChange={(e) => updateRegisterField('age', e.target.value)}
            />
          </label>
          <label>
            Gender
            <select
              value={registerForm.gender}
              onChange={(e) =>
                updateRegisterField('gender', e.target.value as 'male' | 'female')
              }
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </label>
          <label>
            CNIC (13 digits)
            <input
              required
              value={registerForm.cnic}
              onChange={(e) => updateRegisterField('cnic', e.target.value)}
              placeholder="12345-1234567-1"
            />
          </label>
        </div>
      </Modal>

      <ConfirmNoteModal
        open={Boolean(noteTarget)}
        title={noteTarget ? `${noteTarget.action} user` : 'Confirm'}
        message={
          noteTarget
            ? `Apply “${noteTarget.action}” to ${noteTarget.user.fullName}?`
            : ''
        }
        confirmLabel={noteTarget?.action ?? 'Confirm'}
        noteLabel="Optional reason"
        busy={Boolean(noteTarget && busyUserId === noteTarget.user.id)}
        onCancel={() => setNoteTarget(null)}
        onConfirm={confirmLifecycle}
      />
    </section>
  );
}
